import { useCallback, useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { StatusPill } from '@/components/StatusPill';
import { SearchInput } from '@/components/SearchInput';
import {
  TabBody, TabToolbar, TableCard, Th, Td, Tr, SkeletonTable, InlineHint, formStatusVariant, titleCase,
} from '@/components/tiktok/measurementShared';
import { tiktokService } from '@/services/tiktok.service';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { tiktokInstantFormsUrl } from '@/lib/tiktokLinks';
import { ClipboardList, RefreshCw, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

type LeadForm = { pageId: string; title: string; status?: string };

/**
 * Read-only list of the advertiser's Instant Forms.
 *
 * TikTok exposes no create/update/delete endpoint for these (unlike Meta, where
 * the CRM builds lead forms itself), so this page lists what exists and links
 * out to Leads Center for anything else.
 */
type Props = {
  /** Rendered inside TiktokToolsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function TiktokLeadForms({ embedded = false }: Props = {}) {
  const [forms, setForms] = useState<LeadForm[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const { account } = useTiktokAccountCurrency();

  const load = useCallback(async (announce = false) => {
    setRefreshing(true);
    try {
      const rows = await tiktokService.listLeadForms();
      setForms(rows);
      if (announce) {
        toast.success(rows.length === 1 ? '1 Instant Form found' : `${rows.length} Instant Forms found`);
      }
    } catch {
      // axios interceptor toasts the backend message
      setForms([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUrl = tiktokInstantFormsUrl(account?.advertiserId);
  const openTiktok = () => window.open(createUrl, '_blank', 'noopener');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = forms ?? [];
    return q ? list.filter((f) => f.title.toLowerCase().includes(q) || f.pageId.includes(q)) : list;
  }, [forms, query]);

  const loading = forms === null;
  const hasData = !!forms && forms.length > 0;

  const actions = (
    <>
      <Button variant="secondary" onClick={() => load(true)} loading={refreshing}>
        <RefreshCw size={14} /> Refresh
      </Button>
      <Button variant="primary" onClick={openTiktok}>
        <ExternalLink size={14} /> Create form on TikTok
      </Button>
    </>
  );

  return (
    <>
      {!embedded && (
        <Topbar
          title="Instant Forms"
          breadcrumb="TikTok Ads / Instant Forms"
          description="Lead-generation forms hosted by TikTok. Ads with the Leads objective open one of these instead of a website."
        />
      )}

      <TabBody>
        <TabToolbar
          title="Instant Forms"
          count={forms?.length ?? null}
          subtitle="Forms are created in TikTok Ads Manager only (Assets → Leads Center → Instant Form); published forms appear here and are selectable on Leads ads. Leads flow back through the TikTok webhook."
          right={actions}
        />

        {loading && <SkeletonTable rows={4} cols={4} />}

        {!loading && !hasData && (
          <EmptyState
            icon={<ClipboardList size={32} />}
            title="No Instant Forms yet"
            description="Create one in TikTok Ads Manager → Assets → Leads Center → Instant Form: add a name and phone field, set a privacy-policy URL and publish. Then Refresh — it appears here ready to attach to a lead-generation ad."
            action={
              <>
                <Button variant="primary" onClick={openTiktok}><ExternalLink size={14} /> Open Leads Center</Button>
                <Button variant="secondary" onClick={() => load(true)} loading={refreshing}><RefreshCw size={14} /> Refresh</Button>
              </>
            }
          />
        )}

        {hasData && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SearchInput value={query} onChange={setQuery} placeholder="Filter by name or ID…" />
              <InlineHint>The API doesn't return a created date for forms — open a form in Leads Center to see its history.</InlineHint>
            </div>

            <TableCard minWidth={640}>
              <thead>
                <tr>
                  <Th>Form</Th>
                  <Th>Form ID</Th>
                  <Th>Status</Th>
                  <Th>Use</Th>
                  <Th align="right" width="120px"><span className="sr-only">Actions</span></Th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-text-muted">No forms match “{query}”.</td></tr>
                )}
                {visible.map((f) => (
                  <Tr key={f.pageId}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                          <ClipboardList size={15} />
                        </span>
                        <span className="truncate font-medium text-text-primary">{f.title || 'Untitled form'}</span>
                      </div>
                    </Td>
                    <Td mono muted>{f.pageId}</Td>
                    <Td>
                      {f.status
                        ? <StatusPill status={f.status} label={titleCase(f.status)} variant={formStatusVariant(f.status)} />
                        : <span className="text-text-muted">—</span>}
                    </Td>
                    <Td muted>Selectable on lead-generation ads</Td>
                    <Td align="right">
                      <Button size="sm" variant="secondary" onClick={openTiktok} title="Edit in TikTok Leads Center">
                        <ExternalLink size={12} /> Open
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableCard>
          </>
        )}
      </TabBody>
    </>
  );
}
